// /opt/isp_erp/frontend/src/pages/admin/finanzas/DashboardFinanciero.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Paper
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { finanzasAPI } from '../../../api/finanzas/finanzas.api';
import './DashboardFinanciero.css';

// Registrar todos los componentes necesarios de Chart.js
ChartJS.register(
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement, 
  ArcElement, 
  Title, 
  Tooltip, 
  Legend,
  Filler
);

const DashboardFinanciero = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [tipoGrafico, setTipoGrafico] = useState('line');
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    cargarDashboard();
  }, [year, month]);

  const cargarDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await finanzasAPI.getDashboardFinanciero(year, month);
      console.log('=== DATOS COMPLETOS RECIBIDOS ===');
      console.log('Response:', response);
      console.log('Response.data:', response.data);
      console.log('Facturacion mensual:', response.data?.facturacion_mensual);
      console.log('Cobranza mensual:', response.data?.cobranza_mensual);
      
      // Guardar datos de depuración
      setDebugInfo({
        facturacion_mensual: response.data?.facturacion_mensual,
        cobranza_mensual: response.data?.cobranza_mensual,
        tipo_datos: typeof response.data?.facturacion_mensual,
        es_array: Array.isArray(response.data?.facturacion_mensual)
      });
      
      // Transformar datos si es necesario
      let datos = { ...response.data };
      
      // Si facturacion_mensual no es un array o está vacío, crear uno
      if (!datos.facturacion_mensual || !Array.isArray(datos.facturacion_mensual)) {
        console.warn('facturacion_mensual no es un array, creando array por defecto');
        datos.facturacion_mensual = new Array(12).fill(0);
      }
      
      if (!datos.cobranza_mensual || !Array.isArray(datos.cobranza_mensual)) {
        console.warn('cobranza_mensual no es un array, creando array por defecto');
        datos.cobranza_mensual = new Array(12).fill(0);
      }
      
      // Asegurar que tengan 12 elementos
      if (datos.facturacion_mensual.length !== 12) {
        console.warn(`facturacion_mensual tiene ${datos.facturacion_mensual.length} elementos, ajustando a 12`);
        const newArray = new Array(12).fill(0);
        for (let i = 0; i < Math.min(datos.facturacion_mensual.length, 12); i++) {
          newArray[i] = datos.facturacion_mensual[i];
        }
        datos.facturacion_mensual = newArray;
      }
      
      if (datos.cobranza_mensual.length !== 12) {
        console.warn(`cobranza_mensual tiene ${datos.cobranza_mensual.length} elementos, ajustando a 12`);
        const newArray = new Array(12).fill(0);
        for (let i = 0; i < Math.min(datos.cobranza_mensual.length, 12); i++) {
          newArray[i] = datos.cobranza_mensual[i];
        }
        datos.cobranza_mensual = newArray;
      }
      
      console.log('=== DATOS TRANSFORMADOS ===');
      console.log('Facturacion mensual transformada:', datos.facturacion_mensual);
      console.log('Cobranza mensual transformada:', datos.cobranza_mensual);
      
      setDashboardData(datos);
    } catch (err) {
      console.error('Error detallado:', err);
      console.error('Response error:', err.response?.data);
      setError(err.response?.data?.message || 'Error al cargar el dashboard');
      
      // Datos de ejemplo para pruebas
      const datosEjemplo = {
        total_facturado: 125000,
        total_cobrado: 98500,
        saldo_por_cobrar: 26500,
        eficiencia_cobro: 78.8,
        facturacion_mensual: [8500, 9200, 10100, 11500, 10800, 12500, 13200, 12800, 14100, 13500, 14800, 15200],
        cobranza_mensual: [7200, 8100, 8900, 9800, 9500, 10800, 11500, 11200, 12300, 11800, 12900, 13400],
        estados: {
          pagadas: 145,
          pendientes: 32,
          vencidas: 8,
          anuladas: 3
        },
        facturas_vencidas: 8,
        monto_vencido: 12450
      };
      setDashboardData(datosEjemplo);
      setDebugInfo({
        facturacion_mensual: datosEjemplo.facturacion_mensual,
        cobranza_mensual: datosEjemplo.cobranza_mensual,
        es_ejemplo: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Preparar datos para el gráfico - Asegurar que sean números
  const mesesLabels = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  
  const facturacionData = dashboardData?.facturacion_mensual || new Array(12).fill(0);
  const cobranzaData = dashboardData?.cobranza_mensual || new Array(12).fill(0);
  
  // Convertir a números por si acaso vienen como strings
  const facturacionNumeros = facturacionData.map(val => typeof val === 'number' ? val : parseFloat(val) || 0);
  const cobranzaNumeros = cobranzaData.map(val => typeof val === 'number' ? val : parseFloat(val) || 0);
  
  console.log('=== DATOS PARA GRÁFICO ===');
  console.log('Facturación (numérica):', facturacionNumeros);
  console.log('Cobranza (numérica):', cobranzaNumeros);
  console.log('Labels:', mesesLabels);
  
  const chartData = {
    labels: mesesLabels,
    datasets: [
      {
        label: 'Facturado',
        data: facturacionNumeros,
        borderColor: '#2196f3',
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        borderWidth: 2,
        pointBackgroundColor: '#2196f3',
        pointBorderColor: '#fff',
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.3,
        fill: true,
      },
      {
        label: 'Cobrado',
        data: cobranzaNumeros,
        borderColor: '#4caf50',
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        borderWidth: 2,
        pointBackgroundColor: '#4caf50',
        pointBorderColor: '#fff',
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.3,
        fill: true,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          boxWidth: 10
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '$' + value.toLocaleString();
          }
        },
        title: {
          display: true,
          text: 'Monto (USD)',
          font: {
            weight: 'bold'
          }
        }
      },
      x: {
        title: {
          display: true,
          text: 'Mes',
          font: {
            weight: 'bold'
          }
        }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !dashboardData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={cargarDashboard}>
              Reintentar
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  const totalFacturado = dashboardData?.total_facturado || 0;
  const totalCobrado = dashboardData?.total_cobrado || 0;
  const saldoPorCobrar = dashboardData?.saldo_por_cobrar || (totalFacturado - totalCobrado);
  const eficienciaCobro = dashboardData?.eficiencia_cobro || (totalFacturado > 0 ? (totalCobrado / totalFacturado) * 100 : 0);

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e' }}>
          Dashboard Financiero
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={cargarDashboard}
        >
          Actualizar
        </Button>
      </Box>

      {/* Panel de depuración - visible solo en desarrollo */}
      {process.env.NODE_ENV === 'development' && debugInfo && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#fff3e0', overflow: 'auto' }}>
          <Typography variant="subtitle2" color="warning.main">Información de depuración:</Typography>
          <pre style={{ fontSize: '12px', margin: 0 }}>
            {JSON.stringify(debugInfo, null, 2)}
          </pre>
        </Paper>
      )}

      {/* Filtros */}
      <Card sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Año</InputLabel>
              <Select value={year} onChange={(e) => setYear(e.target.value)} label="Año">
                {[2023, 2024, 2025, 2026].map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Mes</InputLabel>
              <Select value={month} onChange={(e) => setMonth(e.target.value)} label="Mes">
                <MenuItem value={1}>Enero</MenuItem>
                <MenuItem value={2}>Febrero</MenuItem>
                <MenuItem value={3}>Marzo</MenuItem>
                <MenuItem value={4}>Abril</MenuItem>
                <MenuItem value={5}>Mayo</MenuItem>
                <MenuItem value={6}>Junio</MenuItem>
                <MenuItem value={7}>Julio</MenuItem>
                <MenuItem value={8}>Agosto</MenuItem>
                <MenuItem value={9}>Septiembre</MenuItem>
                <MenuItem value={10}>Octubre</MenuItem>
                <MenuItem value={11}>Noviembre</MenuItem>
                <MenuItem value={12}>Diciembre</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant={tipoGrafico === 'line' ? 'contained' : 'outlined'}
                onClick={() => setTipoGrafico('line')}
                size="small"
              >
                Línea
              </Button>
              <Button 
                variant={tipoGrafico === 'bar' ? 'contained' : 'outlined'}
                onClick={() => setTipoGrafico('bar')}
                size="small"
              >
                Barras
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* Tarjetas de resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Facturado
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#2196f3', fontWeight: 'bold' }}>
                    ${totalFacturado.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 40, color: '#2196f3', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Cobrado
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                    ${totalCobrado.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <PaymentIcon sx={{ fontSize: 40, color: '#4caf50', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Saldo por Cobrar
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                    ${saldoPorCobrar.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <TrendingDownIcon sx={{ fontSize: 40, color: '#ff9800', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Eficiencia de Cobro
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#9c27b0', fontWeight: 'bold' }}>
                    {eficienciaCobro.toFixed(1)}%
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, color: '#9c27b0', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gráficos principales */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Evolución Facturación vs Cobranza {year}
              </Typography>
              <Box sx={{ height: 400, width: '100%', position: 'relative' }}>
                {tipoGrafico === 'line' ? (
                  <Line data={chartData} options={chartOptions} />
                ) : (
                  <Bar data={chartData} options={chartOptions} />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Distribución por Estado
              </Typography>
              <Box sx={{ height: 300, width: '100%', position: 'relative' }}>
                <Pie 
                  data={{
                    labels: ['Pagadas', 'Pendientes', 'Vencidas', 'Anuladas'],
                    datasets: [{
                      data: [
                        dashboardData?.estados?.pagadas || 0,
                        dashboardData?.estados?.pendientes || 0,
                        dashboardData?.estados?.vencidas || 0,
                        dashboardData?.estados?.anuladas || 0
                      ],
                      backgroundColor: ['#4caf50', '#ff9800', '#f44336', '#9e9e9e'],
                      borderWidth: 2,
                      borderColor: '#fff'
                    }]
                  }} 
                  options={pieOptions} 
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alertas */}
      {(dashboardData?.facturas_vencidas > 0 || dashboardData?.monto_vencido > 0) && (
        <Alert 
          severity="warning" 
          sx={{ mt: 3, borderRadius: 2 }}
          icon={<WarningIcon />}
        >
          <Typography variant="body1">
            ⚠️ <strong>Atención:</strong> Tienes {dashboardData.facturas_vencidas} factura(s) vencida(s) por un total de 
            <strong> ${(dashboardData.monto_vencido || 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}</strong>
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default DashboardFinanciero;
